import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../styles/Topic.css';
import DonationModal from '../components/DonationModal';
import FlagImg from '../components/FlagImg';
import { IconLock, IconUser, IconSparkles, IconExternalLink, IconRefresh, IconCreditCard, IconShield } from '../components/Icons';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const EMOTION_META = {
  anger:      { emoji: '😠', color: '#dc2626' },
  sadness:    { emoji: '😢', color: '#2563eb' },
  solidarity: { emoji: '🤝', color: '#059669' },
  fear:       { emoji: '😨', color: '#d97706' },
  hope:       { emoji: '🌟', color: '#7c3aed' },
  neutral:    { emoji: '💬', color: '#6b7280' },
};

function OpinionFeed({ feed, topicId, onNewOpinion }) {
  const { total = 0, countries = 0, local_count = 0, diaspora_count = 0, intl_count = 0, feed: items = [] } = feed;

  function flag(code) {
    if (!code || code.length !== 2) return '';
    return String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
  }

  function timeAgo(iso) {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  return (
    <section className="opinion-feed-panel">
      <div className="ofp-header">
        <h2 className="ofp-title">Voices on this topic</h2>
        <div className="ofp-stats">
          <span className="ofp-stat"><strong>{total}</strong> voices</span>
          <span className="ofp-sep">·</span>
          <span className="ofp-stat"><strong>{countries}</strong> countries</span>
        </div>
      </div>

      {total > 0 && (
        <div className="ofp-breakdown">
          {local_count > 0 && (
            <span className="ofp-group ofp-group--local">
              {local_count} in-country
            </span>
          )}
          {diaspora_count > 0 && (
            <span className="ofp-group ofp-group--diaspora">
              {diaspora_count} diaspora
            </span>
          )}
          {intl_count > 0 && (
            <span className="ofp-group ofp-group--intl">
              {intl_count} international
            </span>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <p className="ofp-empty">No voices yet. Be the first to speak.</p>
      ) : (
        <div className="ofp-list">
          {items.map((item, i) => {
            const em = EMOTION_META[item.emotion] || EMOTION_META.neutral;
            const sameCountry = item.origin === item.residence;
            return (
              <div key={i} className="ofp-item">
                <span className="ofp-emotion" title={item.emotion} style={{ color: em.color }}>
                  {em.emoji}
                </span>
                <div className="ofp-body">
                  <p className="ofp-phrase">"{item.key_phrase}"</p>
                  <div className="ofp-meta">
                    <span className="ofp-flags" title={`Origin: ${item.origin} → Living in: ${item.residence}`}>
                      {flag(item.origin)}
                      {!sameCountry && <><span className="ofp-arrow">→</span>{flag(item.residence)}</>}
                      {item.is_diaspora && <span className="ofp-tag ofp-tag--diaspora">diaspora</span>}
                      {item.is_local && <span className="ofp-tag ofp-tag--local">local</span>}
                    </span>
                    <span className="ofp-time">{timeAgo(item.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const STANCE_META = {
  critical:    { label: 'Critical',    color: '#dc2626', bg: '#fee2e2' },
  demanding:   { label: 'Demanding',   color: '#d97706', bg: '#fef3c7' },
  skeptical:   { label: 'Skeptical',   color: '#7c3aed', bg: '#ede9fe' },
  supportive:  { label: 'Supportive',  color: '#059669', bg: '#d1fae5' },
  reformist:   { label: 'Reformist',   color: '#2563eb', bg: '#dbeafe' },
  neutral:     { label: 'Neutral',     color: '#6b7280', bg: '#f3f4f6' },
};

function ViewpointPanel({ viewpoints }) {
  const { clusters = [], diaspora_narrative, incountry_narrative, international_narrative,
          diaspora_count = 0, incountry_count = 0, international_count = 0 } = viewpoints;

  const groups = [
    { key: 'incountry',     label: 'In-country voices',    count: incountry_count,    narrative: incountry_narrative },
    { key: 'diaspora',      label: 'Diaspora voices',       count: diaspora_count,     narrative: diaspora_narrative },
    { key: 'international', label: 'International voices',  count: international_count, narrative: international_narrative },
  ].filter(g => g.count > 0);

  return (
    <section className="viewpoint-panel">
      <h2 className="vp-heading">Public viewpoints</h2>
      <p className="vp-subheading">
        Distinct positions being argued — not emotions, but the actual claims people make.
      </p>

      <div className="vp-clusters">
        {clusters.map((c, i) => {
          const meta = STANCE_META[c.stance] || STANCE_META.neutral;
          return (
            <div key={i} className="vp-cluster">
              <div className="vp-cluster-top">
                <span className="vp-label">{c.label}</span>
                <span className="vp-stance-badge" style={{ color: meta.color, background: meta.bg }}>
                  {meta.label}
                </span>
              </div>
              <div className="vp-bar-row">
                <div className="vp-bar-track">
                  <div className="vp-bar-fill" style={{ width: `${c.percentage}%` }} />
                </div>
                <span className="vp-pct">{c.percentage}%</span>
              </div>
              {c.argument_core && (
                <p className="vp-argument">{c.argument_core}</p>
              )}
              {c.representative_quote && (
                <blockquote className="vp-quote">"{c.representative_quote}"</blockquote>
              )}
            </div>
          );
        })}
      </div>

      {groups.length > 0 && (
        <div className="vp-narratives">
          <h3 className="vp-narratives-heading">Perspective by group</h3>
          <div className="vp-narrative-grid">
            {groups.map(g => (
              <div key={g.key} className={`vp-narrative-card vp-narrative-card--${g.key}`}>
                <div className="vp-narrative-header">
                  <span className="vp-narrative-title">{g.label}</span>
                  <span className="vp-narrative-count">{g.count} {g.count === 1 ? 'voice' : 'voices'}</span>
                </div>
                {g.narrative
                  ? <p className="vp-narrative-text">{g.narrative}</p>
                  : <p className="vp-narrative-empty">Not enough voices yet to generate a summary (min 5).</p>
                }
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="vp-privacy-note">
        All viewpoints are AI-synthesised from anonymous opinions. No raw text or identity is ever shown.
      </p>
    </section>
  );
}

function TopicPage({ user, topic, onBack, onSynthesize }) {
  const [opinion, setOpinion] = useState('');
  const [summary, setSummary] = useState(null);
  const [viewpoints, setViewpoints] = useState(null);
  const [opinionFeed, setOpinionFeed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  // Voice recording state
  const MAX_SECS = 60;
  const [voiceLang, setVoiceLang] = useState('en');
  const [recordState, setRecordState] = useState('idle'); // idle | recording | transcribing
  const [recTime, setRecTime] = useState(0);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const isDemo = !topic.id || topic.is_demo;

  useEffect(() => {
    if (isDemo) return;
    fetchSummary();      // eslint-disable-line react-hooks/exhaustive-deps
    fetchViewpoints();   // eslint-disable-line react-hooks/exhaustive-deps
    fetchOpinionFeed();  // eslint-disable-line react-hooks/exhaustive-deps
  }, [topic.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSummary = async () => {
    if (isDemo) return;
    try {
      const response = await axios.get(`${API_BASE}/topics/${topic.id}/summary`);
      setSummary(response.data);
    } catch {
      // no summary yet
    }
  };

  const fetchViewpoints = async () => {
    if (isDemo) return;
    try {
      const response = await axios.get(`${API_BASE}/topics/${topic.id}/viewpoints`);
      setViewpoints(response.data);
    } catch {
      // not yet synthesised
    }
  };

  const fetchOpinionFeed = async () => {
    if (isDemo) return;
    try {
      const response = await axios.get(`${API_BASE}/topics/${topic.id}/opinion-feed`);
      setOpinionFeed(response.data);
    } catch {
      // ignore
    }
  };

  const handleSubmitOpinion = async (e) => {
    e.preventDefault();
    if (!opinion.trim()) return;
    setLoading(true);
    setSubmitMsg(null);
    try {
      await axios.post(`${API_BASE}/opinions?user_id=${user.id}`, {
        topic_id: topic.id,
        content: opinion,
      });
      setOpinion('');
      setSubmitMsg({ type: 'success', text: 'Your voice has been added anonymously.' });
      fetchOpinionFeed(); // eslint-disable-line react-hooks/exhaustive-deps
    } catch (error) {
      setSubmitMsg({ type: 'error', text: error.response?.data?.detail || 'Error submitting opinion.' });
    } finally {
      setLoading(false);
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mrRef.current && mrRef.current.state !== 'inactive') {
      mrRef.current.stop();
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setSubmitMsg({ type: 'error', text: 'Voice recording is not supported in this browser.' });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mrRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };

      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setRecordState('transcribing');
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        const form = new FormData();
        form.append('audio', blob, 'recording.webm');
        form.append('language', voiceLang);
        try {
          const { data } = await axios.post(`${API_BASE}/transcribe`, form);
          setOpinion(data.text);
          setSubmitMsg({ type: 'success', text: 'Transcribed — review and edit below before submitting.' });
        } catch {
          setSubmitMsg({ type: 'error', text: 'Transcription failed. Please type your opinion instead.' });
        } finally {
          setRecordState('idle');
          setRecTime(0);
        }
      };

      mr.start(200);
      setRecordState('recording');
      setRecTime(0);
      timerRef.current = setInterval(() => {
        setRecTime(prev => {
          const next = prev + 1;
          if (next >= MAX_SECS) {
            clearInterval(timerRef.current);
            if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop();
            return next;
          }
          return next;
        });
      }, 1000);
    } catch {
      setSubmitMsg({ type: 'error', text: 'Microphone access denied or unavailable.' });
    }
  };

  const handleSynthesize = async () => {
    setSynthesizing(true);
    try {
      await axios.post(`${API_BASE}/topics/${topic.id}/synthesize`);
      await Promise.all([fetchSummary(), fetchViewpoints()]);
      onSynthesize();
      setSubmitMsg({ type: 'success', text: 'AI synthesis complete.' });
    } catch (error) {
      setSubmitMsg({ type: 'error', text: error.response?.data?.detail || 'Error generating synthesis.' });
    } finally {
      setSynthesizing(false);
    }
  };

  function getCountryFlag(countryCode) {
    if (!countryCode || countryCode.length !== 2) return '';
    return String.fromCodePoint(...countryCode.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
  }

  const sameCountry = user ? user.country_origin === user.country_residence : false;

  function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days > 0) return `${days}d ago`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs > 0) return `${hrs}h ago`;
    return 'just now';
  }

  return (
    <div className="topic-page">
      <button className="back-button" onClick={onBack}>
        ← Back to feed
      </button>

      {isDemo && (
        <div className="demo-topic-banner">
          <strong>Demonstration event</strong> — this is an example to show how Proteste works.
          Real events are published regularly. Register or sign in to participate in live topics.
        </div>
      )}

      <div className="topic-header">
        <div className="topic-title-section">
          <span className="location-flag"><FlagImg code={topic.country_code} size={32} /></span>
          <div>
            <h1>{topic.title}</h1>
            <p className="location">{topic.region}</p>
          </div>
        </div>
        <div className="topic-info">
          <span className="info-badge"><strong>{topic.opinions_count}</strong> voices</span>
          <span className="info-badge">{timeAgo(topic.created_at)}</span>
          {topic.source_type && <span className="info-badge">{topic.source_type}</span>}
        </div>
      </div>

      {topic.description && (
        <p className="topic-description">{topic.description}</p>
      )}

      {topic.image_url && (
        <img
          src={topic.image_url}
          alt=""
          className="topic-hero-image"
          loading="lazy"
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}

      {topic.source_url && (
        <a href={topic.source_url} target="_blank" rel="noopener noreferrer" className="source-link">
          <IconExternalLink size={14} /> View original source
        </a>
      )}

      {submitMsg && (
        <div className={submitMsg.type === 'success' ? 'success-message' : 'error-message'}>
          {submitMsg.text}
        </div>
      )}

      {/* Opinion Submission */}
      <section className="opinion-submission">
        <h2>Share your opinion</h2>

        {/* User identity strip */}
        {user && (
          <div className="submitter-identity">
            <span className="identity-id"><IconLock size={13} /> {user.user_id}</span>
            <span className="identity-sep">·</span>
            <span className="identity-origin" title="Your country of origin">
              <FlagImg code={user.country_origin} size={16} style={{ marginRight: 3 }} /> Origin
            </span>
            {!sameCountry && (
              <>
                <span className="identity-arrow">→</span>
                <span className="identity-residence" title="Your current country of residence">
                  <FlagImg code={user.country_residence} size={16} style={{ marginRight: 3 }} /> Located
                </span>
              </>
            )}
          </div>
        )}

        {isDemo && (
          <div className="submission-note" style={{ opacity: 0.6 }}>
            <span><IconLock size={13} /> This is a demonstration event — participation is not available</span>
          </div>
        )}
        {!user && !isDemo && (
          <div className="submission-note" style={{ opacity: 0.6 }}>
            <span><IconLock size={13} /> Sign in as a user to submit your opinion</span>
          </div>
        )}
        {user && !isDemo && (
        <div className="submission-note">
          <span><IconLock size={13} /> Your opinion stays completely anonymous</span>
          <span><IconUser size={13} /> No name or personal information is ever shared</span>
          <span><IconSparkles size={13} /> Only AI-generated summaries are made public</span>
        </div>
        )}
        {user && !isDemo && <form onSubmit={handleSubmitOpinion} className="opinion-form">
          <div className="voice-row">
            <div className="voice-lang">
              <button type="button" className={`vlang${voiceLang === 'en' ? ' vlang--active' : ''}`} onClick={() => setVoiceLang('en')} disabled={recordState !== 'idle'}>EN</button>
              <button type="button" className={`vlang${voiceLang === 'fr' ? ' vlang--active' : ''}`} onClick={() => setVoiceLang('fr')} disabled={recordState !== 'idle'}>FR</button>
            </div>
            {recordState === 'idle' && (
              <button type="button" className="mic-btn" onClick={startRecording} disabled={loading}>
                <span className="mic-icon">🎤</span> Record voice
              </button>
            )}
            {recordState === 'recording' && (
              <button type="button" className="mic-btn mic-btn--recording" onClick={stopRecording}>
                <span className="mic-pulse" /> Stop&nbsp;·&nbsp;{MAX_SECS - recTime}s left
              </button>
            )}
            {recordState === 'transcribing' && (
              <span className="voice-transcribing">Transcribing…</span>
            )}
            <span className="voice-hint">Max {MAX_SECS}s · FR or EN · text only is submitted</span>
          </div>
          <textarea
            value={opinion}
            onChange={e => setOpinion(e.target.value)}
            placeholder="Share your thoughts about this incident… (or record voice above)"
            disabled={loading || recordState !== 'idle'}
          />
          <div className="form-footer">
            <span className="char-count">{opinion.length} characters</span>
            <button type="submit" disabled={loading || !opinion.trim()}>
              {loading ? 'Submitting…' : 'Submit anonymously'}
            </button>
          </div>
        </form>}
      </section>

      {/* Opinion Feed */}
      {opinionFeed && (
        <OpinionFeed feed={opinionFeed} topicId={topic.id} />
      )}

      {/* AI Synthesis */}
      {summary ? (
        <section className="ai-synthesis">
          <div className="synthesis-header">
            <h2><IconSparkles size={17} /> AI-generated insight</h2>
            <button onClick={handleSynthesize} disabled={synthesizing} className="refresh-btn">
              <IconRefresh size={13} /> {synthesizing ? 'Analyzing…' : 'Refresh'}
            </button>
          </div>

          <div className="summary-text">
            <p>{summary.summary}</p>
            <span className="generated-at">
              Updated: {new Date(summary.updated_at).toLocaleString()}
            </span>
          </div>

          {summary.geographic_perspectives && summary.geographic_perspectives.length > 0 && (
            <div className="geo-perspectives">
              <h3>Perspectives by country</h3>
              <div className="perspectives-list">
                {summary.geographic_perspectives.slice(0, 6).map((p, i) => (
                  <div key={i} className="perspective-row">
                    <span className="perspective-flags">
                      <FlagImg code={p.origin_country} size={16} />
                      {p.residence_country !== p.origin_country && (
                        <><span className="pflag-arrow">→</span><FlagImg code={p.residence_country} size={16} /></>
                      )}
                    </span>
                    <span className="perspective-concern">{p.primary_concern}</span>
                    <span className="perspective-count">{p.opinions_count} voices</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {summary.emotional_landscape && (
            <div className="emotional-landscape">
              <h3>Emotional landscape</h3>
              <div className="emotion-grid">
                {[
                  { key: 'anger', emoji: '😠', label: 'Anger' },
                  { key: 'sadness', emoji: '😢', label: 'Sadness' },
                  { key: 'solidarity', emoji: '🤝', label: 'Solidarity' },
                  { key: 'fear', emoji: '😨', label: 'Fear' },
                  { key: 'hope', emoji: '🌟', label: 'Hope' },
                ].map(({ key, emoji, label }) => (
                  <div key={key} className="emotion">
                    <span className="emoji">{emoji}</span>
                    <span className="label">{label}</span>
                    <strong>{summary.emotional_landscape[key] ?? 0}%</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {summary.key_themes && summary.key_themes.length > 0 && (
            <div className="key-themes">
              <h3>Main themes</h3>
              <div className="themes-list">
                {summary.key_themes.map((theme, idx) => (
                  <div key={idx} className="theme-item">
                    <span className="emoji">{theme.emoji || '💬'}</span>
                    <div className="theme-content">
                      <h4>{theme.title}</h4>
                      <div className="percentage-bar">
                        <div className="percentage-fill" style={{ width: `${theme.percentage}%` }} />
                      </div>
                      <span className="percentage">{theme.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="ai-synthesis">
          <div className="synthesis-header">
            <h2><IconSparkles size={17} /> AI analysis</h2>
            <button onClick={handleSynthesize} disabled={synthesizing} className="refresh-btn">
              <IconRefresh size={13} /> {synthesizing ? 'Analyzing…' : 'Generate insights'}
            </button>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Submit opinions above, then generate the AI synthesis to see themes, emotional landscape, and geographic insights.
          </p>
        </section>
      )}

      {/* Viewpoints */}
      {viewpoints && viewpoints.clusters && viewpoints.clusters.length > 0 && (
        <ViewpointPanel viewpoints={viewpoints} />
      )}

      {/* Donation */}
      <section className="donation-section">
        <h2>Support Proteste</h2>
        <p>
          Help amplify the voices of the unheard. Your donation supports platform operations
          and the AI infrastructure that makes civic synthesis possible.
        </p>
        <button onClick={() => setShowDonation(true)} className="donate-button">
          <IconCreditCard size={17} /> Make a donation
        </button>
        <p className="donation-note"><IconShield size={13} /> 100% transparent · No ads · No data selling</p>
      </section>

      {showDonation && (
        <DonationModal user={user} onClose={() => setShowDonation(false)} />
      )}
    </div>
  );
}

export default TopicPage;
